import { Controller } from '@hotwired/stimulus'
import {
  hasUrlParam,
  localStorageGet,
  isBuilderEditingMode,
  getUrlParam,
  collectGarbage,
} from '../helpers/utils'
import { connectUserData, dispatchAction, getFlow } from '../helpers/controllerHelpers/utils'
import { Context, CustomEvent as CustomEventType, FlowPage } from '../api/models/types'
import { log } from '../helpers/debug'
import { env } from '../helpers/env'
import { cloneDeep } from 'lodash'
import { handleRequest } from '../api/handleRequest'
import FlowController from './flow_controller'
import { logSentryStep } from '../helpers/sentryObservability'
// import { generatePageSelectors, generateElementSelectors } from '../api/styles/selectors'

export default class extends Controller {
  static classes = ['enter', 'exit']
  static values = {
    url: String,
    pageId: String,
    version: String,
    qsValue: String,
    enterDuration: String,
    enterTimingFunction: String,
    exitDuration: String,
    exitTimingFunction: String,
    exitDelay: String,
  }
  pagesValue: Record<string, string> = {}
  originalPagesValue: Record<string, string> = {}
  hasConnected = false
  classTimeout: NodeJS.Timeout | undefined
  _exitTimeout: NodeJS.Timeout | undefined
  //* Page id whose HTML is currently mounted in this.element, used to guard cache snapshots
  renderedPageId: string | undefined
  //* Set when navigation fails to mount, so isTransitionInProgress does not treat a
  //* pageIdValue/renderedPageId mismatch as transitioning forever
  _navErrored = false
  declare qsValueValue: string
  declare pageIdValue: string

  declare hasPageExitClass: boolean
  declare pageExitClass: string
  declare pageEnterClass: string

  declare readonly hasEnterClass: boolean
  declare readonly hasExitClass: boolean
  declare readonly enterClass: string
  declare readonly enterDurationValue: string
  declare readonly exitClass: string
  declare readonly exitDurationValue: string
  declare readonly exitDelayValue: string

  declare readonly urlValue: string
  declare readonly versionValue: string
  declare readonly branchIdValue: string
  declare readonly qsValue: string
  _destroyBox?: ((skipAnimation?: boolean) => void) | undefined
  disconnectUserData: (() => void) | undefined

  connect() {
    this.pagesValue = {}
    this.originalPagesValue = {}
    const startingPageId = this.element.getAttribute('data-starting-page-id')
    const flowId = this.getFlowId()

    logSentryStep({
      scope: 'Page controller',
      functionName: 'connect',
      step: 'page_connect',
      props: {
        flow_id: flowId,
        page_id: this.pageIdValue,
        starting_page_id: startingPageId,
      },
    })

    if (startingPageId) {
      this.pagesValue[startingPageId] = this.element.innerHTML
      this.originalPagesValue[startingPageId] = this.element.innerHTML
      this.renderedPageId = startingPageId
      this.loadUpcomingPages(startingPageId)

      // this.element.removeAttribute('data-starting-page-id')
    }
    this.hasConnected = true
    this.disconnectUserData = connectUserData(this)

    //* The enter class is baked into the initial DOM for first-paint animation — remove it
    //* after the enter duration so the page is not stuck appearing to transition
    if (this.hasEnterClass && this.element.classList.contains(this.enterClass)) {
      clearTimeout(this.classTimeout)
      this.classTimeout = setTimeout(
        () => this.element.classList.remove(this.enterClass),
        parseInt(this.enterDurationValue) || 300
      )
    }

    if (this.pageIdValue !== startingPageId) {
      this.pageIdValueChanged(
        this.pageIdValue,
        typeof startingPageId === 'string' ? startingPageId : undefined
      )
      this.loadUpcomingPages(this.pageIdValue)
    }
  }

  disconnect() {
    if (this.classTimeout) clearTimeout(this.classTimeout)
    if (typeof this.disconnectUserData === 'function') this.disconnectUserData()
  }

  async pageIdValueChanged(pageId: string, prevPageId?: string) {
    //* Trigger our own garbage collection for stimulus event listeners
    setTimeout(() => collectGarbage(), 1000)

    const hasConnected = this.hasConnected
    if (pageId === prevPageId || !hasConnected) return

    //* A fresh navigation re-arms the transition tracking
    this._navErrored = false

    //* Exit transition
    //* If not done already, remove the enter class before doing anything as it gets stuck on the first page
    if (this.hasEnterClass) this.element.classList.remove(this.enterClass)
    const exitAnimationPromise = this.applyExitClass()

    const result = await this.setPageHtml(pageId, prevPageId, [exitAnimationPromise])
    if (result.error) {
      this._navErrored = true
      if (this.hasExitClass) this.element.classList.remove(this.exitClass)
      //* Reset flag on error to allow retry
      this.notifyTransitionComplete()
      return
    }

    const pages = getFlow(this, true)?.pages || []
    const currentPage = pages.find((p: FlowPage) => p.id === pageId)
    if (!currentPage) {
      console.error(`No page found for id ${pageId}`)
      if (this.hasExitClass) this.element.classList.remove(this.exitClass)
      this.notifyTransitionComplete()
      return
    }

    this.element.className = `FormPage ${generatePageSelectors(currentPage).join(' ')}`

    //* Enter transition
    if (this.hasEnterClass) this.applyEnterClass()
    if (this.hasExitClass) this.element.classList.remove(this.exitClass)

    //* Notify flow controller that transition completed
    this.notifyTransitionComplete()

    if (currentPage.conversions) {
      dispatchAction(this, {
        detail: { actions: currentPage.conversions.map((c) => ({ ...c, output: 'conversion' })) },
      })
    }
  }

  /* Public Methods */
  openInfoBox = async (event: CustomEventType) => {
    const infoBoxKey = event.detail && event.detail.value
    if (typeof this._destroyBox === 'function') this._destroyBox(Boolean(infoBoxKey))
    if (!infoBoxKey) return
    this._destroyBox = await this.setUpInfoBox(String(infoBoxKey))
  }

  closeInfoBox = () => {
    if (typeof this._destroyBox === 'function') this._destroyBox()
  }

  /* Private Methods */
  getFlowId() {
    const flowEl = this.element.closest('[data-controller*="flow"]')
    const flowController = flowEl
      ? (this.application.getControllerForElementAndIdentifier(flowEl, 'flow') as FlowController)
      : null
    return flowController?.idValue || 'unknown'
  }

  applyEnterClass() {
    this.element.classList.remove(this.enterClass)
    clearTimeout(this.classTimeout)
    this.element.classList.add(this.enterClass)
    this.classTimeout = setTimeout(
      () => this.element.classList.remove(this.enterClass),
      parseInt(this.enterDurationValue) || 300
    )
  }

  async applyExitClass() {
    //* If we have a delay, wait for it before advancing
    if (this.exitDelayValue)
      await new Promise((resolve) => setTimeout(resolve, parseInt(this.exitDelayValue) || 0))

    if (this.hasExitClass) this.element.classList.add(this.exitClass)
    return new Promise((resolve) => {
      clearTimeout(this._exitTimeout)
      if (this.hasExitClass)
        this._exitTimeout = setTimeout(() => {
          resolve(true)
        }, parseInt(this.exitDurationValue) || 300)
      else resolve(true)
    })
  }

  //* True from nav start (incl. exitDelay, before exit class) until the new page is mounted
  //* and any enter/exit CSS class is cleared
  isTransitionInProgress(): boolean {
    //* A failed mount leaves pageIdValue ahead of renderedPageId with no transition running
    if (this._navErrored) return false
    if (this.renderedPageId != null && this.pageIdValue !== this.renderedPageId) return true
    return (
      (this.hasExitClass && this.element.classList.contains(this.exitClass)) ||
      (this.hasEnterClass && this.element.classList.contains(this.enterClass))
    )
  }

  loadPage(pageId: string): Promise<string> | undefined {
    if (pageId === undefined || this.urlValue === undefined) return

    if (!this.pagesValue[pageId]) {
      const qsAddons = (this.qsValueValue && this.qsValueValue.split('&')) || []
      const baseUrl = `${this.urlValue}/page/${pageId}`
      if (this.versionValue) qsAddons.push(`version=${this.versionValue}`)
      if (this.branchIdValue) qsAddons.push(`embeddable_branch=${this.branchIdValue}`)
      if (isBuilderEditingMode() && !hasUrlParam('savvy_editing', 'no_sidebar'))
        qsAddons.push('savvy_editing=true')
      const embeddableDraftId = getUrlParam('embeddable_draft')
      if (embeddableDraftId) qsAddons.push(`embeddable_draft=${embeddableDraftId}`)

      const qs = qsAddons.length ? `?${qsAddons.join('&')}` : ''
      let url = `${baseUrl}${qs}`

      const flowId = this.urlValue.split('/').slice(-1)[0]
      const loadedJson = {
        groupId: this.element
          ?.closest('[data-controller*="flow"]')
          ?.getAttribute('data-flow-group-id-value'),
        flowId,
        flow: getFlow(this, true),
      }
      const request = {
        url: new URL(url),
        method: localStorageGet(`SAVVY_FLOW_JSON-${flowId}`) ? 'POST' : 'GET',
        headers: new Headers(
          env() === 'ci' && window.location.origin ? { 'Host-Origin': window.location.origin } : {}
        ),
        body: localStorageGet(`SAVVY_FLOW_JSON-${flowId}`)
          ? { json: JSON.parse(localStorageGet(`SAVVY_FLOW_JSON-${flowId}`) || '{}') }
          : {
              json: loadedJson,
            },
      }

      if (isBuilderEditingMode()) {
        // Include latest version of flow in request, so that new pages are still supported here
        // @TODO: Figure out whether the flow has changed and use that as the condition, instead of isBuilderEditingMode
        // (Because you can change the flow without isBuilderEditingMode)

        request.method = 'POST'
        request.body = {
          json: loadedJson,
        }
      }

      const context: Context = {
        env: 'local',
        fetch: (...args: any[]) => fetch(args[0], args[1]),
        flowsBucket: {},
      }

      return handleRequest(context, request).then(({ responseData: pageHtml }) => {
        this.pagesValue[pageId] = pageHtml
        this.originalPagesValue[pageId] = pageHtml
        return pageHtml
      })
    } else {
      return new Promise((resolve) => resolve(this.pagesValue[pageId]))
    }
  }

  async setPageHtml(
    pageId: string,
    prevPageId?: string,
    promisesToWaitForBeforeChangingHtml?: Promise<any>[]
  ) {
    let hasErrored = false
    if (prevPageId !== undefined) {
      try {
        //* Only snapshot the outgoing page when it is genuinely mounted to avoid poisoning the cache during rapid navigation.
        if (prevPageId && this.renderedPageId === prevPageId)
          this.pagesValue[prevPageId] = this.element.innerHTML
            // Hacky fix for the fact that the page-id is being changed to the next page on components
            // before the page is actually changed, meaning that the page-id is wrong when the user goes back to this page.
            .split(`data-component-page-id-value="${pageId}"`)
            .join(`data-component-page-id-value="${prevPageId}"`)
        const page = await this.loadPage(pageId)

        if (pageId === this.pageIdValue) {
          //* Before changing the page, wait for any promises that need to be resolved
          if (promisesToWaitForBeforeChangingHtml)
            await Promise.allSettled(promisesToWaitForBeforeChangingHtml)

          this.element.innerHTML = String(page)
          this.renderedPageId = pageId

          this.element.getAttributeNames().forEach((attrKey) => {
            // replace the page-id attribute with the new page-id
            const oldVal = this.element.getAttribute(attrKey)
            if (oldVal && oldVal.includes(prevPageId) && attrKey !== 'data-starting-page-id') {
              this.element.setAttribute(attrKey, oldVal.replace(prevPageId, pageId))
            }

            //* I remove transitions attrs and add them back from contents of the page
            if (
              attrKey === 'data-page-enter-class' ||
              attrKey === 'data-page-enter-duration-value' ||
              attrKey === 'data-page-exit-class' ||
              attrKey === 'data-page-exit-duration-value'
            ) {
              this.element.removeAttribute(attrKey)
            }
          })

          //* If page contents has transitions attr I apply them into the element
          const pageContents = this.element.querySelector('.ElementType-PageContents')
          if (pageContents && pageContents.getAttribute('data-page-enter-class'))
            this.element.setAttribute(
              'data-page-enter-class',
              pageContents.getAttribute('data-page-enter-class') || ''
            )
          if (pageContents && pageContents.getAttribute('data-page-enter-duration-value'))
            this.element.setAttribute(
              'data-page-enter-duration-value',
              pageContents.getAttribute('data-page-enter-duration-value') || ''
            )
          if (pageContents && pageContents.getAttribute('data-page-exit-class'))
            this.element.setAttribute(
              'data-page-exit-class',
              pageContents.getAttribute('data-page-exit-class') || ''
            )
          if (pageContents && pageContents.getAttribute('data-page-exit-duration-value'))
            this.element.setAttribute(
              'data-page-exit-duration-value',
              pageContents.getAttribute('data-page-exit-duration-value') || ''
            )

          //* Trigger event for page html updated
          window?.dispatchEvent(
            new CustomEvent('embeddables:page_change_html_updated', {
              detail: {
                current_page_id: pageId,
                previous_page_id: prevPageId,
              },
            })
          )
        } else {
          console.log(
            'Aborting setPageHtml - more recent change than this one',
            pageId,
            this.pageIdValue
          )
        }
      } catch (error) {
        console.error(error)
        hasErrored = true
      }
    }

    this.loadUpcomingPages(pageId)
    return { error: hasErrored }
  }

  loadUpcomingPages(pageId: string) {
    const nextPageId = this._getNextPageId(pageId)
    // @TODO: Get all the next pages until there's one without conditions?
    const upcomingInfoboxPages = this._getUpcomingInfoboxPages(pageId)
    const allUpcomingPages = [nextPageId, ...upcomingInfoboxPages]
    log('allUpcomingPages', allUpcomingPages as unknown as string)
    allUpcomingPages.forEach((_pageId) => {
      if (_pageId) this.loadPage(`${_pageId}`)
    })
  }

  resetAllPageHtmls() {
    this.pagesValue = cloneDeep(this.originalPagesValue)
  }

  // @TODO: Reorganise public vs private methods
  _getNextPageId(pageId: string) {
    const pages = getFlow(this).pages || []
    const nextPageIndex = pages.findIndex((p) => p.id === pageId) + 1
    const nextPage = pages[nextPageIndex]
    return nextPage ? pages[nextPageIndex].id : null
  }

  _getUpcomingInfoboxPages(pageId: string) {
    const flow = getFlow(this, true)
    const pages = flow.pages || []
    const page = pages.find((p) => p.id === pageId)
    const components = (page?.components || [])
      .filter((c) => c.type === 'CustomButton' && c.action === 'open-info-box')
      .map((c) => c.info_box_key) as string[]
    const otherComponents = (page?.components || [])
      .filter(
        (c) =>
          (c.type === 'RichText' || c.type === 'CustomHTML' || c.type === 'RichTextMarkdown') &&
          (c.text || '').includes('<a href="open_popup:')
      )
      .map((c) => (c.text || '').match(/<a href="open_popup:(.*?)">/g))
      .reduce(
        (acc, strs) =>
          // @ts-ignore
          (acc || []).concat(strs?.map((str) => str.match(/<a href="open_popup:(.*?)">/i)[1])),
        []
      ) as RegExpMatchArray
    return components
      .concat(otherComponents)
      .map((pageKey) => (pages.find((p) => p.key === pageKey) || {}).id || null)
      .filter((_pageId) => _pageId) as string[]
  }

  async setUpInfoBox(pageKey: string) {
    const mountElement = this.element.parentElement
    const flow = getFlow(this, true)
    const pages = flow?.pages || []
    const targetPage = pages.find((p) => p.key === pageKey)
    const currentPage = pages.find((p) => p.id === this.pageIdValue)
    if (!targetPage) {
      console.warn(`No Page found for key ${pageKey}`)
      return
    }

    const pageId = targetPage.id
    const pageHTML = await this.loadPage(pageId)

    //* If the flow has the infobox_use_dialog_element attribute set to true, use a dialog element for accessibility, otherwise use a div.
    const useDialog = flow?.infobox_use_dialog_element === true
    const infoBoxContainer = document.createElement('form')
    const infoBoxSubmitHandler = (e: Event) => {
      e.preventDefault()
      e.stopPropagation()
    }
    infoBoxContainer.addEventListener('submit', infoBoxSubmitHandler)
    const infoBoxBackground = document.createElement('div')
    const infoBoxBox = document.createElement(useDialog ? 'dialog' : 'div')
    const closeButton = document.createElement(useDialog ? 'button' : 'div')
    if (useDialog && closeButton instanceof HTMLButtonElement) closeButton.type = 'button'
    const infoBoxPage = document.createElement('div')

    const classes = ['FormPage', ...generatePageSelectors(targetPage)]
    infoBoxPage.classList.add(...classes)

    infoBoxPage.setAttribute(`data-controller`, 'transition')
    infoBoxPage.setAttribute(`data-transition-enter-class`, this.pageEnterClass)
    infoBoxPage.setAttribute(`data-transition-exit-class`, this.pageExitClass)
    infoBoxPage.innerHTML = String(pageHTML)

    closeButton.classList.add('close')
    closeButton.setAttribute('data-controller', 'icon')
    closeButton.setAttribute('data-icon-library-value', 'feather')
    closeButton.setAttribute('data-icon-name-value', 'x')

    infoBoxBox.classList.add(`InfoBox-box`)
    infoBoxBox.appendChild(closeButton)
    infoBoxBox.appendChild(infoBoxPage)

    infoBoxBackground.classList.add(`InfoBox-background`)

    const closeBoxHandler = () => destroyBox()

    const destroyBox = (skipAnimation?: boolean) => {
      this.dispatch('info-box', { detail: { value: null } })
      if (this.hasPageExitClass && !skipAnimation) {
        infoBoxPage.classList.add(this.pageExitClass)
        clearTimeout(this._exitTimeout)
        this._exitTimeout = setTimeout(() => infoBoxContainer.remove(), 150)
      } else infoBoxContainer.remove()

      const actionsOnClose = currentPage?.outputs_onclose_infobox
      if (Array.isArray(actionsOnClose))
        dispatchAction(this, { detail: { actions: actionsOnClose } })

      if (infoBoxContainer) infoBoxContainer.removeEventListener('submit', infoBoxSubmitHandler)
      if (closeButton) closeButton.removeEventListener('click', closeBoxHandler)
      if (infoBoxBackground) infoBoxBackground.removeEventListener('click', closeBoxHandler)
      if (useDialog && infoBoxBox instanceof HTMLDialogElement) {
        infoBoxBox.removeEventListener('close', closeBoxHandler)
      }
    }

    closeButton.addEventListener('click', closeBoxHandler)
    infoBoxBackground.addEventListener('click', closeBoxHandler)

    if (useDialog && infoBoxBox instanceof HTMLDialogElement) {
      infoBoxBox.addEventListener('close', closeBoxHandler)
    }

    infoBoxContainer.classList.add(`InfoBox`)
    infoBoxContainer.setAttribute(`data-flow-target`, 'infobox')
    infoBoxContainer.appendChild(infoBoxBackground)
    infoBoxContainer.appendChild(infoBoxBox)

    mountElement?.appendChild(infoBoxContainer)
    if (useDialog && infoBoxBox instanceof HTMLDialogElement) {
      setTimeout(() => {
        infoBoxContainer.classList.add(`showing`)
        infoBoxBox.showModal()
      }, 0)
    } else {
      setTimeout(() => infoBoxContainer.classList.add(`showing`), 0)
    }

    this.dispatch('info-box', { detail: { value: pageKey } })

    const actionsOnOpen = currentPage?.outputs_onopen_infobox
    if (Array.isArray(actionsOnOpen)) dispatchAction(this, { detail: { actions: actionsOnOpen } })

    return destroyBox
  }

  cancelSubmitEvent(event: Event) {
    event.stopPropagation()
    event.preventDefault()
  }

  //* Notify flow controller that page transition completed
  notifyTransitionComplete() {
    const flowEl = this.element.closest('[data-controller*="flow"]')
    if (!flowEl) return
    const flowController = this.application.getControllerForElementAndIdentifier(
      flowEl,
      'flow'
    ) as any
    //* Reset flag only if syncPagesWithUrl is active
    if (flowController && flowController.syncPagesWithUrl) {
      flowController.awaitingPageChange = false
    }
  }
}

function generatePageSelectors(page: FlowPage) {
  if (!page) return []
  const bases = { PageKey: [page.key], PageTag: page.tags || [] }

  const classes = Object.entries(bases).reduce(
    (acc, [key, values]) => {
      values.forEach((v) => acc.push(`${key}-${v}`))
      return acc
    },
    ['Flow-Page']
  )
  return classes
}
